import {QuoteStore} from "../../../../../quote/store/QuoteStore";
import {EquityOrderType, EquityWizardData, isSameWizardData} from "../models/EquityWizardData";
import {Observable, ReplaySubject, Scheduler} from "rxjs";
import {
    addChildOrder,
    removeChildOrder,
    adviceChange,
    EquityWizardDataChange,
    expiryChange,
    instructionChange,
    lifetimeChange,
    limitChange,
    limitToMidChange,
    nameChange,
    orderTypeChange, parentOrderChange,
    portfolioChange,
    quantityChange,
    quoteChange,
    securityChange,
    sideChange, stopLossLimitChange, stopLossToggleChange, totalValueChange, updateChildOrder
} from "../models/EquityWizardDataChange";
import {AccountSource} from "../../../../../account/types/AccountSource";
import {processUpdate} from "./util/WizardDataChangeUtil";
import {isNotNull} from "../../../../../js/util/UtilFunctions";
import {buildQuoteRequest} from "../../../../../quote/util/QuoteStoreUtil";
import {CodeAndExchange, securityMatcher} from "../../../../../instruments/types/CodeAndExchange";
import {OrderLifetime, OrderSide} from "../../../../../orders/models/OrderRecord";
import {OrderAdvice} from "../../place-order-wizard/models/OrderAdvice";
import {AccountBriefRecord} from "../../../../../account/types/AccountBriefRecord";
import {PricingInstruction} from "../../../../../orders/models/PricingInstruction";
import {PriceStrategy} from "../../../../../components/inputs/price/input/PriceStrategy";
import {ChildOrder} from "../../place-order-wizard/models/ChildOrder";

export class EquityOrderWizardStore {

    private readonly current$ = new ReplaySubject<EquityWizardData>(1);
    private readonly changes$ = new ReplaySubject<EquityWizardDataChange>(1);
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    public constructor(private readonly quoteStore: QuoteStore,
                       private readonly accountSource: AccountSource,
                       initial: EquityWizardData) {
        this.init(initial);
    }

    private init(initial: EquityWizardData) {
        this.watchChanges(initial);
        this.watchQuote();
    }

    private watchChanges(initial: EquityWizardData) {
        this.changes$
            .takeUntil(this.destroyed$)
            .scan((acc, next) => processUpdate(acc, next), initial)
            .startWith(initial)
            .subscribe((data) => {
                console.log("Order Changed", data);
                this.current$.next(data);
            });
    }

    private watchQuote() {
        const security$ = this.current$
                              .map((c) => this.extractSecurity(c))
                              .filter(isNotNull)
                              .distinctUntilChanged(securityMatcher);
        const priceInstruction$ = this.current$
                              .map((c) => c.pricingInstruction)
                              .filter(isNotNull)
                              .distinctUntilChanged((a, b) => a === b);
        const portfolioType$ = this.accountSource
                                   .current
                                   .filter(isNotNull)
                                   .map((a) => a.portfolioType);
        const source$ = Observable.combineLatest(security$, portfolioType$, priceInstruction$);
        source$.takeUntil(this.destroyed$)
               .map(([security, portfolioType]) => buildQuoteRequest(security, portfolioType))
               .switchMap((req) => this.quoteStore.stream(req))
               .map((q) => quoteChange(q))
               .subscribe((change) => this.changes$.next(change));
    }

    private extractSecurity(data: EquityWizardData): CodeAndExchange | null {
        if (data.code != null && data.exchange != null) {
            return {
                code: data.code,
                exchange: data.exchange
            };
        } else {
            return null;
        }
    }

    public updateSecurity(security: CodeAndExchange, strategy: PriceStrategy) {
        console.debug("Updated security", security);
        const change = securityChange(security, strategy);
        this.changes$.next(change);
    }

    public updateName(name: string) {
        console.debug("Updated name", name);
        const change = nameChange(name);
        this.changes$.next(change);
    }

    public updateSide(side: OrderSide) {
        console.debug("Updated side", side);
        const change = sideChange(side);
        this.changes$.next(change);
    }

    public updateQuantity(quantity: number) {
        console.debug("Updated quantity", quantity);
        const change = quantityChange(quantity);
        this.changes$.next(change);
    }

    public updateLimit(limit: number) {
        console.debug("Updated limit", limit);
        const change = limitChange(limit);
        this.changes$.next(change);
    }

    public updatePricingInstruction(pricingInstruction: PricingInstruction) {
        console.debug("Updated Pricing instruction", pricingInstruction);
        const change = instructionChange(pricingInstruction);
        this.changes$.next(change);
    }

    public updateTotalValue(totalValue: number) {
        console.debug("Updated Total value", totalValue);
        const change = totalValueChange(totalValue);
        this.changes$.next(change);
    }

    public updateLimitToMid() {
        console.debug("Updated limit to mid");
        const change = limitToMidChange();
        this.changes$.next(change);
    }

    public updateLifetime(lifetime: OrderLifetime) {
        console.debug("Updated lifetime");
        const change = lifetimeChange(lifetime);
        this.changes$.next(change);
    }

    public updateIsParentOrder(isParentOrder: boolean) {
        console.debug("Updated isParentOrder");
        const change = parentOrderChange(isParentOrder);
        this.changes$.next(change);
    }

    public addChildOrder(childOrder: ChildOrder) {
        console.debug("Added child order");
        const change = addChildOrder(childOrder);
        this.changes$.next(change);
    }

    public removeChildOrder(index: number) {
        console.debug("Removed child order");
        const change = removeChildOrder(index);
        this.changes$.next(change);
    }

    public updateChildOrder(index: number, childOrder: ChildOrder) {
        console.debug("Updated child order");
        const change = updateChildOrder(index, childOrder);
        this.changes$.next(change);
    }

    public updateExpiry(expiry: Date | null) {
        console.debug("Updated expiry", expiry);
        const change = expiryChange(expiry);
        this.changes$.next(change);
    }

    public updateOrderType(type: EquityOrderType) {
        console.debug("Updated type", type);
        const change = orderTypeChange(type);
        this.changes$.next(change);
    }

    public updateAdvice(advice: OrderAdvice | null) {
        console.debug("Updated advice", advice);
        const change = adviceChange(advice);
        this.changes$.next(change);
    }

    public updatePortfolio(portfolio: AccountBriefRecord) {
        console.debug("Updated portfolio", portfolio);
        const change = portfolioChange(portfolio);
        this.changes$.next(change);
    }

    public toggleStopLoss() {
        console.debug("Toggling Stop Loss");
        const change = stopLossToggleChange();
        this.changes$.next(change);
    }

    public changeStopLossLimit(limit: number) {
        console.debug("Changing Stop Loss Limit", limit);
        const change = stopLossLimitChange(limit);
        this.changes$.next(change);
    }

    public get current(): Observable<EquityWizardData> {
        return this.current$
                   .observeOn(Scheduler.async)
                   .distinctUntilChanged(isSameWizardData);
    }

    public destroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
